import { useEffect, useState, useMemo } from "react";
import { Loader2, X, CalendarRange, ShoppingBag, IndianRupee, Printer, Download } from "lucide-react";
import { api, formatMoney } from "@/lib/api";
import OrderStatusBadge from "@/components/OrderStatusBadge";

const PRESETS = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom Date" },
];

const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const dayEnd = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const fmt = (d) => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

function rangeFor(preset, from, to) {
  const now = new Date();
  if (preset === "today") return [dayStart(now), dayEnd(now)];
  if (preset === "yesterday") { const y = new Date(now); y.setDate(y.getDate() - 1); return [dayStart(y), dayEnd(y)]; }
  if (preset === "week") { const s = new Date(now); const day = (s.getDay() + 6) % 7; s.setDate(s.getDate() - day); return [dayStart(s), dayEnd(now)]; }
  if (preset === "month") { const s = new Date(now.getFullYear(), now.getMonth(), 1); return [dayStart(s), dayEnd(now)]; }
  if (preset === "custom") return [from ? dayStart(new Date(from)) : null, to ? dayEnd(new Date(to)) : null];
  return [null, null];
}

export default function SalesmanOrdersModal({ salesman, onClose }) {
  const [orders, setOrders] = useState(null);
  const [preset, setPreset] = useState("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    api.get("/orders", { params: { taken_by: salesman.id } })
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]));
  }, [salesman.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const [start, end] = rangeFor(preset, from, to);

  const filtered = useMemo(() => {
    if (!orders) return [];
    return orders.filter((o) => {
      const t = new Date(o.created_at).getTime();
      if (start && t < start.getTime()) return false;
      if (end && t > end.getTime()) return false;
      return true;
    });
  }, [orders, start, end]);

  const totalValue = filtered.reduce((a, o) => a + (o.total || 0), 0);

  const rangeLabel = preset === "all"
    ? "All Time"
    : (start && end)
      ? (fmt(start) === fmt(end) ? fmt(start) : `${fmt(start)} — ${fmt(end)}`)
      : "Select a date range";

  const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const money = (v) => (v === 0 ? "₹0" : (formatMoney(v) || "₹0"));

  const printReport = () => {
    const logo = `${window.location.origin}/slm-logo.png`;
    const ordersHtml = filtered.map((o) => {
      const rows = o.items.map((it) => `
        <tr>
          <td>${esc(it.name)}${it.is_free ? " <b>(FREE)</b>" : ""}</td>
          <td>${esc(it.unit || "—")}</td>
          <td class="c">${esc(it.qty)}</td>
          <td class="r">${it.is_free ? "₹0" : money(it.price)}</td>
          <td class="r">${it.is_free ? "₹0" : money(it.line_total)}</td>
        </tr>`).join("");
      return `
        <div class="order">
          <div class="ohead">
            <div>
              <div class="shop">${esc(o.shop_name || o.customer_name || "—")}${o.shop_name && o.customer_name ? ` <span class="muted">· ${esc(o.customer_name)}</span>` : ""}</div>
              <div class="muted">Mobile: ${esc(o.customer_phone || "—")} &nbsp;|&nbsp; Order #${esc(o.id.slice(-6).toUpperCase())} &nbsp;|&nbsp; ${esc(new Date(o.created_at).toLocaleString("en-IN"))}</div>
            </div>
            <div class="status">${esc(o.status)}</div>
          </div>
          <table class="items">
            <thead><tr><th>Product</th><th>Unit</th><th class="c">Qty</th><th class="r">Unit Price</th><th class="r">Line Total</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="ototal">Order Total: <b>${money(o.total)}</b></div>
        </div>`;
    }).join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Salesman Order Report</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; color: #1e293b; margin: 24px; }
        .head { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #1d4ed8; padding-bottom: 12px; }
        .head img { height: 56px; }
        .head h1 { margin: 0; font-size: 20px; color: #1e3a8a; }
        .head p { margin: 2px 0 0; font-size: 12px; color: #64748b; }
        .meta { margin: 14px 0; font-size: 13px; }
        .meta b { color: #0f172a; }
        .summary { display: flex; gap: 24px; margin: 10px 0 18px; padding: 10px 14px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; font-size: 14px; }
        .summary .v { font-size: 18px; font-weight: 800; color: #1d4ed8; }
        .order { border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 12px; padding: 10px 12px; page-break-inside: avoid; }
        .ohead { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; margin-bottom: 6px; }
        .shop { font-weight: 700; font-size: 14px; }
        .muted { color: #64748b; font-size: 11px; font-weight: normal; }
        .status { font-size: 11px; font-weight: 700; text-transform: uppercase; background: #f1f5f9; padding: 2px 8px; border-radius: 999px; white-space: nowrap; }
        table.items { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 4px; }
        table.items th { text-align: left; background: #f8fafc; padding: 5px 8px; border-bottom: 1px solid #e2e8f0; font-size: 10px; text-transform: uppercase; color: #64748b; }
        table.items td { padding: 5px 8px; border-bottom: 1px solid #f1f5f9; }
        .c { text-align: center; } .r { text-align: right; }
        .ototal { text-align: right; margin-top: 6px; font-size: 13px; }
        .grand { margin-top: 16px; padding-top: 10px; border-top: 2px solid #1d4ed8; display: flex; justify-content: space-between; font-size: 15px; font-weight: 800; }
        @media print { body { margin: 12mm; } }
      </style></head><body>
      <div class="head">
        <img src="${logo}" alt="SLM" onerror="this.style.display='none'"/>
        <div><h1>Sri Lakshmi Agencies</h1><p>Ramakuppam</p></div>
      </div>
      <div class="meta">
        <div><b>Salesman Order Report</b></div>
        <div>Salesman: <b>${esc(salesman.name)}</b></div>
        <div>Date Range: <b>${esc(rangeLabel)}</b></div>
        <div class="muted">Generated: ${esc(new Date().toLocaleString("en-IN"))}</div>
      </div>
      <div class="summary">
        <div>Total Orders<div class="v">${filtered.length}</div></div>
        <div>Grand Total Order Value<div class="v">${money(totalValue)}</div></div>
      </div>
      ${filtered.length ? ordersHtml : '<p style="text-align:center;color:#94a3b8">No orders in this period.</p>'}
      <div class="grand"><span>Grand Total (${filtered.length} orders)</span><span>${money(totalValue)}</span></div>
      </body></html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div data-testid="salesman-orders-modal" className="relative flex max-h-[88vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 p-5">
          <div>
            <h3 className="text-lg font-bold text-slate-800">{salesman.name}</h3>
            <p className="text-xs text-slate-500">Salesman order report</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="salesman-print-report"
              onClick={printReport}
              disabled={!filtered.length}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
            <button
              data-testid="salesman-download-pdf"
              onClick={printReport}
              disabled={!filtered.length}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Download PDF
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Filters */}
        <div className="border-b border-slate-100 p-5">
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`salesman-date-${p.key}`}
                onClick={() => setPreset(p.key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  preset === p.key ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {preset === "custom" && (
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">From Date</label>
                <input data-testid="salesman-from-date" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">To Date</label>
                <input data-testid="salesman-to-date" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
              </div>
            </div>
          )}

          {/* Selected range + totals */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span data-testid="salesman-range-label" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
              <CalendarRange className="h-4 w-4" /> {rangeLabel}
            </span>
            <span data-testid="salesman-total-orders" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700">
              <ShoppingBag className="h-4 w-4 text-blue-400" /> {filtered.length} orders
            </span>
            <span data-testid="salesman-total-value" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-700">
              <IndianRupee className="h-4 w-4" /> {formatMoney(totalValue) || "₹0"}
            </span>
          </div>
        </div>

        {/* Orders list */}
        <div className="overflow-y-auto p-5">
          {orders === null ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No orders in this period.</p>
          ) : (
            <div className="space-y-3" data-testid="salesman-orders-list">
              {filtered.map((o) => (
                <div key={o.id} data-testid={`salesman-order-${o.id}`} className="rounded-lg border border-slate-100 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {o.shop_name ? <>{o.shop_name} <span className="font-normal text-slate-400">· {o.customer_name}</span></> : (o.customer_name || "—")}
                      </p>
                      <p className="text-xs text-slate-500">📱 {o.customer_phone || "—"}</p>
                      <p className="text-xs text-slate-400">#{o.id.slice(-6).toUpperCase()} · {new Date(o.created_at).toLocaleString("en-IN")}</p>
                    </div>
                    <OrderStatusBadge status={o.status} />
                  </div>
                  <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                    {o.items.map((it, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{it.is_free ? "🎁 " : ""}{it.name}{it.unit ? ` · ${it.unit}` : ""} × {it.qty}</span>
                        <span>{it.is_free ? "₹0" : formatMoney(it.line_total)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex justify-between border-t border-slate-50 pt-2 text-sm font-bold text-slate-800">
                    <span>Total</span><span className="text-blue-700">{formatMoney(o.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
